/**
 * /ko/voice-to-text — 음성 텍스트 변환 pillar reframe bridge (Korean).
 *
 * Targets 음성 텍스트 변환 (2.6k) / 음성파일 텍스트 변환 (1.7k). Reframe: 전사본은
 * 원재료, Draftlize는 의사결정을 살아있는 제품 컨텍스트로.
 */
import { MarketingShell } from './MarketingShell';

export function KoVoiceToText() {
  return (
    <MarketingShell
      currentLocale="ko"
      hreflangPath="/voice-to-text"
      hreflangLocales={['ko']}
      documentTitle="음성 텍스트 변환 완벽 가이드｜무료 변환, 그다음은? · Draftlize"
      documentDescription="음성·녹음 파일을 무료로 텍스트로 변환하는 방법과, 더 중요한 다음 단계——전사본에서 진짜 중요한 '의사결정'을 뽑아내는 법."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">가이드 · 음성 텍스트 변환</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            음성 텍스트 변환——<br />
            <em>전사본은 끝이 아니다.</em>
          </h1>
          <p className="lede">
            녹음을 넣으면 몇 초 만에 깔끔한 전사본이 나옵니다. 네이버 클로바, Notta 같은 도구는 "말한 것을 빠짐없이 받아쓰는" 일을 정말 잘합니다. 계속 쓰세요. 하지만 전사본은 <em>원재료</em>이지 결과물이 아닙니다. 그 회의에서 진짜 중요했던 것——내린 <em>의사결정</em>과 그 이유——은 수천 자 속에 묻히고, 아무도 다시 열지 않는 문서에 가라앉아 몇 주 만에 낡습니다. 이 페이지는 전사본의 '다음 단계'에 관한 것입니다.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">무료로 음성을 텍스트로 변환하기, 3단계.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>파일 선택</strong>——MP3, M4A, WAV 또는 음성 메모.</li>
            <li><strong>변환 실행</strong>——AI 음성 텍스트 변환 도구가 음성을 텍스트로 바꿔 줍니다. 대부분 무료 한도가 있습니다.</li>
            <li><strong>교정</strong>——이름·전문 용어를 한 번 훑고 내보내기.</li>
          </ol>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '22px 0 0' }}>
            음성·녹음 파일 모두 같은 방식입니다. 문제는, 전사본이 정확할수록 글자 더미가 커지고 그만큼 핵심 결정이 깊이 묻힌다는 것. '받아쓰기'는 더 이상 병목이 아닙니다. <em>'뽑아내기'</em>가 병목입니다.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">원재료 vs 살아있는 컨텍스트</span>
            <h2 className="serif">전사본이 끝나는 곳에서 <em>일이 시작된다.</em></h2>
          </div>
          <p>음성 텍스트 변환 도구와 Draftlize는 하는 일이 다릅니다. 하나는 대화를 기록하고, 다른 하나는 그 안의 결정을 계속 유효하게 유지합니다. 둘 다 쓰세요.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>음성 텍스트 변환 도구</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>역할</td><td>말한 것을 기록</td><td className="ours yes">결정한 것을 최신으로 유지</td></tr>
            <tr><td>출력</td><td className="partial">전사본 + 요약</td><td className="ours yes">그래프 속 의사결정 카드</td></tr>
            <tr><td>새 결정이 이전 결정을 뒤집음</td><td className="no">무관한 두 개의 메모</td><td className="ours yes">연결됨; 옛 결정은 stale 표시</td></tr>
            <tr><td>3주 후</td><td className="no">문서에 묻힘</td><td className="ours yes">참조 가능·최신 또는 표시됨</td></tr>
            <tr><td>AI 에이전트가 사용</td><td className="partial">평문을 읽음</td><td className="ours yes">MCP로 읽고 씀</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">의사결정을 <em>살아있는 컨텍스트</em>로.</h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            기록은 도구에 맡기세요. 핵심은 텍스트 속 몇 개의 결정에 무슨 일이 일어나는가입니다——Draftlize에서 그것들은 요약의 한 줄이기를 멈추고, 늘 참인 그래프가 됩니다. 문서를 요약했다면 <a href="/ko/pdf-summary" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PDF 요약 가이드</a>도 같은 맥락입니다.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">결정이 '줄'이 아니라 '카드'가 된다</h3>
            <p>전사본이나 요약을 붙여 넣으면 그 안의 결정이 타입이 있는 카드가 됩니다——결정, 이유, 검토한 대안, 담당자. 한 시간의 대화가 '살아남아야 할' 몇 개로 압축되고, 각각 ID로 참조됩니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">새 결정이 뒤집힌 옛 결정을 stale로</h3>
            <p>이번 주 회의가 지난달 결정을 바꿉니다. 서로 연결되어 있어 새 결정이 옛 결정과 그 위의 스펙을 자동으로 stale로 표시합니다. 다음 회의는 이미 정해진 것을 다시 논쟁하지 않습니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI 에이전트가 '결정된 것'을 읽는다</h3>
            <p>회의의 결정이 Claude Code나 Cursor가 MCP로 읽는 컨텍스트가 됩니다——다음 스펙을 쓰는 에이전트는 팀이 화요일에 무엇을, 왜 결정했는지 처음부터 압니다.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          도구는 회의를 완벽하게 받아씁니다. 다만 3주 뒤, 그 안의 어떤 결정이 아직 유효한지는 알려주지 못합니다.
        </blockquote>
        <cite>음성 텍스트 변환 도구는 그대로. 결정을 계속 살아있게 하는 건 Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">자주 묻는 질문</span>
            <h2 className="serif">FAQ.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '무료 음성 텍스트 변환 도구가 있나요?', a: '클로바노트, Notta 등 대부분 무료 한도가 있습니다. Draftlize는 음성 텍스트 변환을 하지 않습니다——전사본이 나온 뒤, 그 안의 결정을 구조화된 최신 카드로 만드는 역할입니다.' },
            { q: '정확도는 어떤가요?', a: '최근 AI 음성 텍스트 변환은 한국어에서도 실용적입니다. 다만 정확할수록 글자가 많아져 핵심 결정이 묻히므로, 결정을 뽑아내는 단계가 중요합니다.' },
            { q: '변환한 뒤 어떻게 활용하나요?', a: '요약을 Draftlize에 붙여 넣고 결정을 카드로 남깁니다——결정, 이유, 담당자. 전체 전사본을 저장하는 게 아니라, 회의보다 오래 남아야 할 결정을 참조 가능·최신으로 유지하는 것이 목표입니다.' },
            { q: '메모 앱에 저장하는 것과 뭐가 다른가요?', a: '메모에 저장하면 결정이 관련 스펙과 단절된 평문으로 남아 조용히 낡습니다. Draftlize는 그것들을 연결하고, 생각이 바뀌면 무엇이 stale가 되는지 자동으로 표시합니다.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.85, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">전사본을, 결정으로 <em>—— $5 무료로.</em></h2>
        </div>
        <div className="trust-strip">
          <span>신규 가입 시 $5 지급</span>
          <span>사용한 만큼만 결제</span>
          <span>잔액은 만료되지 않음</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            음성 텍스트 변환 도구는 그대로 쓰세요. 요약을 Draftlize에 붙여 넣으면 그 안의 결정이 살아있는 카드가 됩니다——연결되고, 최신이며, AI 에이전트도 읽습니다.
          </p>
          <a className="btn-primary" href="/">$5 무료로 시작하기</a>
        </div>
      </section>
    </MarketingShell>
  );
}
